<script setup lang="ts">
import { ArrowLeft } from 'lucide-vue-next'
import type { RouteLocationRaw } from 'vue-router'

import type { HeaderCount } from '@/composables/headerCount'

interface Parent {
  label: string
  to: RouteLocationRaw
}

const props = withDefaults(
  defineProps<{
    title: string
    parent: Parent
    count?: HeaderCount | null
    unitKey?: string | null
  }>(),
  { count: null, unitKey: null }
)

const { value: countValue, label: countLabel } = useHeaderCount(
  () => props.count,
  () => props.unitKey
)
</script>

<template>
  <div class="flex flex-col gap-4 px-6 pb-6 pt-6 sm:px-12 sm:pb-8 sm:pt-8 lg:flex-row lg:items-end lg:justify-between">
    <div class="min-w-0">
      <router-link
        :to="parent.to"
        class="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft class="size-4" />
        {{ parent.label }}
      </router-link>

      <div class="mt-2 flex items-center gap-3">
        <h1 class="truncate text-3xl font-bold tracking-tight text-foreground">{{ title }}</h1>
        <Badge v-if="countValue !== null" variant="neutral" class="shrink-0 text-sm" :title="countLabel ?? undefined">
          {{ countValue }}
        </Badge>
        <slot name="badge" />
      </div>
    </div>

    <div v-if="$slots.actions" class="flex shrink-0 items-center gap-2">
      <slot name="actions" />
    </div>
  </div>
</template>
